// Invoices & Contracts ledger.
//
// Treats every AWARDED procurement event as a contract that may have
// invoices flowing against it. Phase 1 view: awarded events table with
// vendor, contract value, status, contract type, award date.
//
// Backend:
//   GET /api/bead-pm/projects/:id/procurement-events

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';

type Project = { id: string; project_name: string; project_code: string };
type ProcurementEvent = {
  id: string;
  event_code: string;
  procurement_type: string;
  threshold_tier: 'micro' | 'simplified' | 'formal';
  description: string;
  estimated_amount_usd: string | number;
  awarded_vendor_name: string | null;
  awarded_vendor_uei: string | null;
  award_amount_usd: string | number | null;
  award_date: string | null;
  contract_type: string | null;
  status: string;
};

const STATUS_BADGE: Record<string, { bg: string; fg: string; label: string }> = {
  planned:    { bg: '#f1f5f9', fg: '#475569', label: 'Planned' },
  soliciting: { bg: '#dbeafe', fg: '#1e40af', label: 'Soliciting' },
  evaluating: { bg: '#fef3c7', fg: '#92400e', label: 'Evaluating' },
  awarded:    { bg: '#d1fae5', fg: '#065f46', label: 'Awarded' },
  cancelled:  { bg: '#fee2e2', fg: '#991b1b', label: 'Cancelled' },
};

const CONTRACT_TYPE_LABEL: Record<string, string> = {
  firm_fixed_price:   'Firm fixed price',
  cost_reimbursable:  'Cost-reimbursable',
  time_and_materials: 'Time & materials',
  iqc:                'IQC / IDIQ',
  other:              'Other',
};

const fmtUSD = (n: any) => {
  if (n == null || n === '') return '—';
  const v = Number(n);
  if (!isFinite(v)) return '—';
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}k`;
  return `$${Math.round(v).toLocaleString()}`;
};

function StatCard({ label, value, sub, color }: { label: string; value: string | number; sub?: string; color?: string }) {
  return (
    <div style={{
      background: '#fff', border: '1px solid var(--border, #e2e8f0)',
      borderTop: `3px solid ${color ?? '#3b82f6'}`,
      borderRadius: 6, padding: '12px 14px',
    }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
        letterSpacing: '.06em', color: 'var(--text-muted, #64748b)' }}>{label}</div>
      <div style={{ fontSize: 24, fontWeight: 800, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--text-muted, #64748b)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  const s = STATUS_BADGE[status] ?? STATUS_BADGE.planned;
  return (
    <span style={{
      background: s.bg, color: s.fg, padding: '2px 8px',
      borderRadius: 999, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
    }}>{s.label}</span>
  );
}

export default function InvoicesPage() {
  const [projectId, setProjectId] = useState<string | null>(null);

  const { data: projects } = useQuery<Project[]>({
    queryKey: ['bead-projects'],
    queryFn: () => api.get('/bead/projects'),
  });
  const activeProjectId = projectId ?? projects?.[0]?.id ?? null;

  const { data: eventsData } = useQuery<{ items: ProcurementEvent[] }>({
    queryKey: ['procurement-events', activeProjectId],
    queryFn: () => api.get(`/bead-pm/projects/${activeProjectId}/procurement-events`),
    enabled: !!activeProjectId,
  });
  const events: ProcurementEvent[] = eventsData?.items ?? [];

  // Awarded events become contracts.
  const contracts = useMemo(() => events.filter(e => e.status === 'awarded'), [events]);

  const kpis = useMemo(() => {
    const total = contracts.length;
    const totalAwarded = contracts.reduce((acc, e) => acc + Number(e.award_amount_usd ?? 0), 0);
    const ffp = contracts.filter(e => e.contract_type === 'firm_fixed_price').length;
    const open = events.filter(e => ['planned', 'soliciting', 'evaluating'].includes(e.status)).length;
    return { total, totalAwarded, ffp, open };
  }, [contracts, events]);

  return (
    <div>
      <div className="page__header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div>
          <h1 className="page__title" style={{ margin: 0, fontSize: 24 }}>Invoices & Contracts</h1>
          <div className="page__subtitle" style={{ color: 'var(--text-muted, #64748b)', fontSize: 13 }}>
            Awarded contracts from procurement, ready to receive invoices and roll into draws
          </div>
        </div>
        <select
          value={activeProjectId ?? ''}
          onChange={(e) => setProjectId(e.target.value || null)}
          style={{ padding: '6px 10px', fontSize: 13, border: '1px solid var(--border, #e2e8f0)', borderRadius: 6 }}
        >
          {(projects ?? []).map(p => (
            <option key={p.id} value={p.id}>{p.project_name} ({p.project_code})</option>
          ))}
        </select>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 16 }}>
        <StatCard label="Awarded contracts" value={kpis.total} color="#10b981" />
        <StatCard label="Total committed"   value={fmtUSD(kpis.totalAwarded)} color="#0f172a" />
        <StatCard label="Firm fixed price"  value={kpis.ffp} sub={`of ${kpis.total} contracts`} color="#3b82f6" />
        <StatCard label="Open procurements" value={kpis.open} sub="not yet awarded" color={kpis.open > 0 ? '#f59e0b' : '#10b981'} />
      </div>

      <h2 style={{ fontSize: 16, marginBottom: 8 }}>Contracts ledger · {contracts.length}</h2>
      <div style={{ background: '#fff', border: '1px solid var(--border, #e2e8f0)', borderRadius: 6, overflow: 'hidden', marginBottom: 16 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead style={{ background: '#f8fafc', borderBottom: '1px solid var(--border, #e2e8f0)' }}>
            <tr>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Contract #</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Vendor</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Description</th>
              <th style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Value</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Type</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Awarded</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '.06em', color: '#64748b' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {contracts.length === 0 ? (
              <tr><td colSpan={7} style={{ padding: 24, color: '#94a3b8', textAlign: 'center' }}>
                No awarded contracts yet. Awarded procurement events show up here.
                {' '}<Link to="/procurement" style={{ color: '#3b82f6' }}>Go to Procurement →</Link>
              </td></tr>
            ) : contracts.map(c => (
              <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={{ padding: '8px 10px', fontFamily: 'ui-monospace, monospace', fontSize: 11, color: '#0f172a', fontWeight: 600 }}>{c.event_code}</td>
                <td style={{ padding: '8px 10px' }}>
                  <div style={{ fontWeight: 500 }}>{c.awarded_vendor_name ?? '—'}</div>
                  {c.awarded_vendor_uei && <div style={{ fontSize: 10, color: '#94a3b8' }}>UEI {c.awarded_vendor_uei}</div>}
                </td>
                <td style={{ padding: '8px 10px', fontSize: 12, maxWidth: 320 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.description}>{c.description}</div>
                </td>
                <td style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtUSD(c.award_amount_usd)}</td>
                <td style={{ padding: '8px 10px', fontSize: 12, color: '#64748b' }}>{c.contract_type ? CONTRACT_TYPE_LABEL[c.contract_type] ?? c.contract_type : '—'}</td>
                <td style={{ padding: '8px 10px', fontSize: 11, color: '#64748b' }}>{c.award_date ?? '—'}</td>
                <td style={{ padding: '8px 10px' }}><StatusBadge status={c.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 11, color: '#64748b', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <span>Need to log a new procurement? <Link to="/procurement" style={{ color: '#3b82f6' }}>Procurement →</Link></span>
        <span>Federal-share drawdown view: <Link to="/draws" style={{ color: '#3b82f6' }}>Draws →</Link></span>
        <span>Vendor + insurance details: <Link to="/vendors" style={{ color: '#3b82f6' }}>Vendors →</Link></span>
      </div>
    </div>
  );
}
